import { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { UserPlus, PencilSimple, Trash, Shield, Users as UsersIcon, Camera, Upload, X } from '@phosphor-icons/react';
import api, { getErrorMessage } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../App';

const ROLE_COLORS = {
  owner: 'bg-purple-100 text-purple-800 border-purple-200',
  admin: 'bg-red-100 text-red-800 border-red-200',
  manager: 'bg-blue-100 text-blue-800 border-blue-200',
  receptionist: 'bg-green-100 text-green-800 border-green-200',
  staff: 'bg-gray-100 text-gray-800 border-gray-200'
};

const ROLE_DESCRIPTIONS = {
  owner: 'Highest level - full system access and all privileges',
  admin: 'Full system access - manage users, settings, all features',
  manager: 'Manage operations - customers, batches, reports (no user management)',
  receptionist: 'Front desk - check-in, view customers, limited access',
  staff: 'View only - see customers, batches, attendance'
};

const AVAILABLE_MENUS = [
  { id: 'dashboard', name: 'Dashboard', description: 'Home page with overview' },
  { id: 'customers', name: 'Customers', description: 'Manage customer records' },
  { id: 'deleted', name: 'Deleted', description: 'Customers auto-removed after 20 days without check-in (restore here)' },
  { id: 'batches', name: 'Batches', description: 'Schedule and manage therapy batches' },
  { id: 'batch-schedule', name: 'Batch Schedule', description: 'View batch and bed allocation schedule' },
  { id: 'premium-schedule', name: 'Premium Schedule', description: '5-bed schedule for Purchased customers' },
  { id: 'checkin', name: 'Check-In', description: 'Check-in/check-out customers' },
  { id: 'waiting-list', name: 'Waiting List', description: 'View queue and batch allocations' },
  { id: 'bmi', name: 'BMI', description: 'Body Composition Analysis and BMI tracking' },
  { id: 'tracking', name: 'Tracking', description: 'View client tracking and session notes' },
  { id: 'daily-check-in', name: 'Daily Check-In', description: 'Daily attendance summary and category-wise breakdown' },
  { id: 'staff-login-logout', name: 'Staff Login/Logout', description: 'View staff login and logout history' },
  { id: 'attendance', name: 'Attendance Report', description: 'View attendance records' },
  { id: 'analytics', name: 'Analytics', description: 'Reports and insights' },
  { id: 'ai-assistant', name: 'AI Assistant', description: 'AI-powered chatbot for health queries and content creation' },
  { id: 'stock-items', name: 'Stock Items', description: 'Manage product catalog (Admin only)' },
  { id: 'stock', name: 'Stock Management', description: 'Manage inventory and transactions' },
  { id: 'company-ledger', name: 'Company Ledger', description: 'Manage purchases and payments' },
  { id: 'sales-records', name: 'Sales Records', description: 'Manage sales, invoices, and collections' },
  { id: 'petty-cash', name: 'Petty Cash & Expenses', description: 'Track petty cash and daily expenses with receipts' },
  { id: 'balance', name: 'Balance', description: 'View and manage financial balance' },
  { id: 'notifications', name: 'Notifications', description: 'Send WhatsApp reminders' },
  { id: 'notifications-absent', name: 'Absent Calls', description: 'AI-powered calls to absent customers' },
  { id: 'counselling', name: 'Counselling', description: 'AI-powered customer counselling with complete health history' },
  { id: 'ai-settings', name: 'AI Settings', description: 'Configure AI Guide training content (Admin only)' },
  { id: 'branches', name: 'Branch Management', description: 'Manage branch locations (Admin only)' },
  { id: 'users', name: 'User Management', description: 'Manage staff accounts (Admin/Manager)' }
];

// Menus only an Admin may grant; hidden from every other creator (Owner, Manager, Staff, Receptionist)
const ADMIN_ONLY_GRANT_MENUS = ['stock-items', 'stock', 'company-ledger', 'sales-records', 'ai-settings', 'branches', 'users'];
const visibleMenusFor = (creatorRole) =>
  creatorRole === 'admin' ? AVAILABLE_MENUS : AVAILABLE_MENUS.filter(m => !ADMIN_ONLY_GRANT_MENUS.includes(m.id));

const ROLE_DEFAULT_PERMISSIONS = {
  owner: ['dashboard', 'customers', 'deleted', 'batches', 'batch-schedule', 'premium-schedule', 'checkin', 'waiting-list', 'bmi', 'tracking', 'daily-check-in', 'staff-login-logout', 'attendance', 'notifications', 'notifications-absent', 'counselling', 'analytics', 'ai-assistant', 'stock-items', 'stock', 'company-ledger', 'sales-records', 'petty-cash', 'balance', 'branches', 'users'],
  admin: ['dashboard', 'customers', 'deleted', 'batches', 'batch-schedule', 'premium-schedule', 'checkin', 'waiting-list', 'bmi', 'tracking', 'daily-check-in', 'staff-login-logout', 'attendance', 'notifications', 'notifications-absent', 'counselling', 'analytics', 'ai-assistant', 'ai-settings', 'stock-items', 'stock', 'company-ledger', 'sales-records', 'petty-cash', 'users'],
  manager: ['dashboard', 'customers', 'deleted', 'batches', 'batch-schedule', 'premium-schedule', 'checkin', 'waiting-list', 'bmi', 'tracking', 'daily-check-in', 'staff-login-logout', 'attendance', 'notifications', 'notifications-absent', 'counselling', 'analytics', 'stock', 'sales-records', 'petty-cash'],
  receptionist: ['dashboard', 'customers', 'deleted', 'batches', 'batch-schedule', 'premium-schedule', 'checkin', 'waiting-list', 'daily-check-in', 'staff-login-logout', 'attendance', 'sales-records', 'petty-cash'],
  staff: ['dashboard', 'customers', 'deleted', 'batches', 'batch-schedule', 'premium-schedule', 'waiting-list', 'bmi', 'tracking', 'daily-check-in', 'staff-login-logout', 'attendance', 'sales-records', 'petty-cash']
};

const Users = () => {
  const { user } = useAuth();
  const { activeBranch } = useBranch();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'staff',
    permissions: [],
    assigned_branches: [],
    photo: null
  });

  const [showWebcam, setShowWebcam] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  const fetchBranches = useCallback(async () => {
    try {
      const response = await api.get('/branches');
      setBranches(response.data);
    } catch (error) {
      console.error('Failed to load branches');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  // /api/branches already returns only the branches the current user may access
  const getAvailableBranches = () => branches;

  // Get available roles for assignment based on current user's role
  const getAvailableRoles = () => {
    const currentUserRole = user?.role;
    
    switch (currentUserRole) {
      case 'admin':
        // Admin can assign all roles
        return ['owner', 'admin', 'manager', 'receptionist', 'staff'];
      
      case 'owner':
        // Owner can assign: owner, manager, receptionist, staff (NOT admin)
        return ['owner', 'manager', 'receptionist', 'staff'];
      
      case 'manager':
        // Manager can assign: owner, manager, receptionist, staff (NOT admin)
        return ['owner', 'manager', 'receptionist', 'staff'];
      
      case 'receptionist':
        // Receptionist can assign: staff only
        return ['staff'];
      
      default:
        // Staff or unknown - no role assignment allowed
        return [];
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchBranches();
  }, [fetchUsers, fetchBranches]);

  const visibleMenus = visibleMenusFor(user?.role);
  const visibleMenuIds = visibleMenus.map(m => m.id);

  // When role changes, set default permissions (limited to menus the creator may grant)
  useEffect(() => {
    if (showAddDialog && formData.role) {
      setFormData(prev => ({
        ...prev,
        permissions: (ROLE_DEFAULT_PERMISSIONS[formData.role] || []).filter(p => visibleMenuIds.includes(p))
      }));
    }
  }, [showAddDialog, formData.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', formData);
      toast.success('User created successfully!');
      setShowAddDialog(false);
      setFormData({ email: '', name: '', password: '', role: 'staff', permissions: [], assigned_branches: [], photo: null });
      setPhotoPreview(null);
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create user'));
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        name: formData.name,
        role: formData.role,
        permissions: formData.permissions,
        assigned_branches: formData.assigned_branches
      };
      if (formData.password) {
        updateData.password = formData.password;
      }
      if (formData.photo) {
        updateData.photo = formData.photo;
      }
      
      await api.put(`/users/${selectedUser.user_id}`, updateData);
      toast.success('User updated successfully!');
      setShowEditDialog(false);
      setSelectedUser(null);
      setFormData({ email: '', name: '', password: '', role: 'staff', permissions: [], assigned_branches: [], photo: null });
      setPhotoPreview(null);
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update user'));
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete user'));
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      password: '',
      role: user.role,
      permissions: user.permissions || [],
      assigned_branches: user.assigned_branches || [],
      photo: user.photo || null
    });
    setPhotoPreview(user.photo ? `${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${user.photo}` : null);
    setShowEditDialog(true);
  };

  const togglePermission = (menuId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(menuId)
        ? prev.permissions.filter(p => p !== menuId)
        : [...prev.permissions, menuId]
    }));
  };

  const toggleBranch = (branchId) => {
    setFormData(prev => ({
      ...prev,
      assigned_branches: prev.assigned_branches.includes(branchId)
        ? prev.assigned_branches.filter(b => b !== branchId)
        : [...prev.assigned_branches, branchId]
    }));
  };

  const selectAllBranches = () => {
    setFormData(prev => ({
      ...prev,
      assigned_branches: getAvailableBranches().map(b => b.branch_id)
    }));
  };

  const clearAllBranches = () => {
    setFormData(prev => ({
      ...prev,
      assigned_branches: []
    }));
  };

  const selectAllPermissions = () => {
    setFormData(prev => ({
      ...prev,
      permissions: visibleMenuIds
    }));
  };

  const clearAllPermissions = () => {
    setFormData(prev => ({
      ...prev,
      permissions: []
    }));
  };

  // Photo handling functions
  const startWebcam = async () => {
    try {
      setShowWebcam(true);
      await new Promise(resolve => setTimeout(resolve, 100)); // Wait for DOM
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: { ideal: 'environment' }, // Use back camera on mobile/tablet (better quality)
          width: { ideal: 1280 }, 
          height: { ideal: 720 } 
        } 
      });
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsInline', '');
        videoRef.current.setAttribute('autoplay', '');
        videoRef.current.muted = true;
        
        // Wait for video to be ready
        await new Promise((resolve) => {
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().then(resolve).catch(err => {
              console.error('Play error:', err);
              resolve();
            });
          };
        });
      }
    } catch (error) {
      toast.error('Could not access webcam. Please check camera permissions.');
      console.error('Webcam error:', error);
      setShowWebcam(false);
    }
  };

  const stopWebcam = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowWebcam(false);
  };

  const capturePhoto = async () => {
    if (videoRef.current && videoRef.current.videoWidth > 0) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      
      // Flip the image back to normal (since video is mirrored)
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      
      // Convert canvas to blob and upload
      canvas.toBlob(async (blob) => {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append('file', blob, 'profile-photo.jpg');
          
          const response = await api.post('/storage/upload/profile', formDataUpload, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          const storagePath = response.data.storage_path;
          setFormData(prev => ({ ...prev, photo: storagePath }));
          setPhotoPreview(`${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${storagePath}`);
          stopWebcam();
          toast.success('Photo captured!');
        } catch (error) {
          console.error('Photo upload failed:', error);
          toast.error('Failed to upload photo. Please try again.');
        }
      }, 'image/jpeg', 0.8);
    } else {
      toast.error('Camera not ready. Please wait a moment and try again.');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size should be less than 10MB');
        return;
      }
      
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await api.post('/storage/upload/profile', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const storagePath = response.data.storage_path;
        setFormData(prev => ({ ...prev, photo: storagePath }));
        setPhotoPreview(`${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${storagePath}`);
        toast.success('Photo uploaded!');
      } catch (error) {
        console.error('Photo upload failed:', error);
        toast.error('Failed to upload photo. Please try again.');
      }
    }
  };

  const removePhoto = () => {
    setFormData(prev => ({ ...prev, photo: null }));
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!user || !['owner', 'admin', 'manager'].includes(user.role)) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-[#1C1C1A] mb-2">Access Denied</h2>
            <p className="text-[#1C1C1A]/60">You don&apos;t have permission to access user management.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="users-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-[#2C4C3B] mb-2">
              User Management
            </h1>
            <p className="text-sm sm:text-base text-[#1C1C1A]/60">
              Manage staff accounts (Admin/Manager/owner)
            </p>
          </div>
          <Button
            data-testid="add-user-button"
            onClick={() => setShowAddDialog(true)}
            className="bg-[#2C4C3B] hover:bg-[#2C4C3B]/90 w-full sm:w-auto"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            Add User
          </Button>
        </div>

        <Card className="p-6 bg-white border-[#E5E5DF]">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead className="bg-[#E2E8E4]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Name</th>
                  <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Email</th>
                  <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Role</th>
                  <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Branches</th>
                  <th className="px-6 py-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5DF]">
                {users.map((u) => (
                  <tr key={u.user_id} className="hover:bg-[#F9F9F8] transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {u.photo ? (
                          <img 
                            src={u.photo.startsWith('ceracare-crm/') 
                              ? `${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${u.photo}` 
                              : u.photo} 
                            alt={u.name}
                            className="w-10 h-10 rounded-full object-cover border-2 border-[#2C4C3B]"
                          />
                        ) : (
                          <div className="flex items-center justify-center w-10 h-10 bg-[#2C4C3B] text-white rounded-full font-semibold">
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className="font-medium text-[#2C4C3B]">{u.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[#1C1C1A]/60">{u.email}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${ROLE_COLORS[u.role]}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {u.assigned_branches && u.assigned_branches.length > 0 ? (
                          u.assigned_branches.map((branchId) => {
                            const branch = branches.find(b => b.branch_id === branchId);
                            return branch ? (
                              <span
                                key={branchId}
                                className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-800 border border-blue-200"
                              >
                                {branch.name}
                              </span>
                            ) : null;
                          })
                        ) : (
                          <span className="text-xs text-[#1C1C1A]/40 italic">No branches</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => openEditDialog(u)}
                          variant="outline"
                          size="sm"
                          className="hover:bg-blue-50"
                          data-testid={`edit-user-${u.user_id}`}
                        >
                          <PencilSimple className="w-4 h-4" />
                        </Button>
                        {u.user_id !== user?.user_id && (
                          <Button
                            onClick={() => handleDeleteUser(u.user_id, u.name)}
                            variant="outline"
                            size="sm"
                            className="hover:bg-red-50 text-red-600"
                            data-testid={`delete-user-${u.user_id}`}
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Add User Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Add New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddUser} className="space-y-4">
              {/* Photo Upload Section */}
              <div className="border-b border-[#E5E5DF] pb-4">
                <Label className="text-base font-semibold mb-3 block">User Photo (Optional)</Label>
                
                {!photoPreview && !showWebcam && (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={startWebcam}
                      className="flex-1"
                    >
                      <Camera size={20} className="mr-2" />
                      Take Photo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1"
                    >
                      <Upload size={20} className="mr-2" />
                      Upload Photo
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </div>
                )}

                {showWebcam && (
                  <div className="space-y-3">
                    <div className="relative bg-black rounded-lg overflow-hidden" style={{ minHeight: '256px' }}>
                      <video
                        ref={videoRef}
                        className="w-full h-64 object-cover"
                        autoPlay
                        playsInline
                        muted
                        style={{ transform: 'scaleX(-1)' }}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={capturePhoto}
                        className="flex-1 bg-[#2C4C3B]"
                      >
                        <Camera size={20} className="mr-2" />
                        Capture
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={stopWebcam}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {photoPreview && (
                  <div className="space-y-3">
                    <div className="relative inline-block">
                      <img
                        src={photoPreview}
                        alt="User preview"
                        className="w-32 h-32 rounded-full object-cover border-4 border-[#2C4C3B]"
                      />
                      <button
                        type="button"
                        onClick={removePhoto}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X size={16} weight="bold" />
                      </button>
                    </div>
                    <p className="text-xs text-green-600">✓ Photo ready</p>
                  </div>
                )}
              </div>

              <div>
                <Label>Full Name</Label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="••••••••"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableRoles().includes('owner') && <SelectItem value="owner">Owner</SelectItem>}
                    {getAvailableRoles().includes('admin') && <SelectItem value="admin">Admin</SelectItem>}
                    {getAvailableRoles().includes('manager') && <SelectItem value="manager">Manager</SelectItem>}
                    {getAvailableRoles().includes('receptionist') && <SelectItem value="receptionist">Receptionist</SelectItem>}
                    {getAvailableRoles().includes('staff') && <SelectItem value="staff">Staff</SelectItem>}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#1C1C1A]/60 mt-1">{ROLE_DESCRIPTIONS[formData.role]}</p>
                {user?.role !== 'admin' && (
                  <p className="text-xs text-orange-600 mt-1">
                    ⓘ You can only assign: {getAvailableRoles().join(', ')}
                  </p>
                )}
              </div>
              
              <div className="border-t border-[#E5E5DF] pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Menu Permissions</Label>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={selectAllPermissions}>
                      Select All
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={clearAllPermissions}>
                      Clear All
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-[#1C1C1A]/60 mb-3">
                  Select which menus this user can access (leave empty to use role defaults)
                </p>
                <div className="space-y-2 border border-[#E5E5DF] rounded-md p-4 bg-[#F9F9F8]">
                  {visibleMenus.map((menu) => (
                    <label
                      key={menu.id}
                      className="flex items-start gap-3 p-3 hover:bg-white rounded-md cursor-pointer border border-transparent hover:border-[#2C4C3B] transition-all"
                    >
                      <input
                        type="checkbox"
                        checked={(formData.permissions || []).includes(menu.id)}
                        onChange={() => togglePermission(menu.id)}
                        className="mt-1 w-4 h-4 text-[#2C4C3B] rounded focus:ring-[#2C4C3B]"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm text-[#2C4C3B]">{menu.name}</p>
                        <p className="text-xs text-[#1C1C1A]/60">{menu.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-blue-600 font-medium mt-2">
                  ✓ {(formData.permissions || []).length} of {visibleMenus.length} menus selected
                </p>
              </div>

              <div className="border-t border-[#E5E5DF] pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Branch Assignment</Label>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={selectAllBranches}>
                      Select All
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={clearAllBranches}>
                      Clear All
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-[#1C1C1A]/60 mb-3">
                  Select which branches this user can access
                  {!isAdmin && ' (limited to your assigned branches)'}
                </p>
                {getAvailableBranches().length === 0 ? (
                  <div className="text-center py-6 text-[#1C1C1A]/60 text-sm border border-[#E5E5DF] rounded-md bg-[#F9F9F8]">
                    No branches available. {isAdmin ? 'Create branches first in Branch Management.' : 'Contact admin to assign branches to you.'}
                  </div>
                ) : (
                  <div className="space-y-2 border border-[#E5E5DF] rounded-md p-4 bg-[#F9F9F8]">
                    {getAvailableBranches().map((branch) => (
                      <label
                        key={branch.branch_id}
                        className="flex items-start gap-3 p-3 hover:bg-white rounded-md cursor-pointer border border-transparent hover:border-[#2C4C3B] transition-all"
                      >
                        <input
                          type="checkbox"
                          checked={(formData.assigned_branches || []).includes(branch.branch_id)}
                          onChange={() => toggleBranch(branch.branch_id)}
                          className="mt-1 w-4 h-4 text-[#2C4C3B] rounded focus:ring-[#2C4C3B]"
                          data-testid={`branch-checkbox-${branch.branch_id}`}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-sm text-[#2C4C3B]">{branch.name}</p>
                          {branch.address && <p className="text-xs text-[#1C1C1A]/60">{branch.address}</p>}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                <p className="text-xs text-blue-600 font-medium mt-2">
                  ✓ {(formData.assigned_branches || []).length} of {getAvailableBranches().length} branches selected
                </p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#2C4C3B] hover:bg-[#2C4C3B]/90">
                  Create User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Edit User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              {/* Photo Upload Section */}
              <div className="border-b border-[#E5E5DF] pb-4">
                <Label className="text-base font-semibold mb-3 block">User Photo (Optional)</Label>
                
                {!photoPreview && !showWebcam && (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={startWebcam}
                      className="flex-1"
                    >
                      <Camera size={20} className="mr-2" />
                      Take Photo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1"
                    >
                      <Upload size={20} className="mr-2" />
                      Upload Photo
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </div>
                )}

                {showWebcam && (
                  <div className="space-y-3">
                    <div className="relative bg-black rounded-lg overflow-hidden" style={{ minHeight: '256px' }}>
                      <video
                        ref={videoRef}
                        className="w-full h-64 object-cover"
                        autoPlay
                        playsInline
                        muted
                        style={{ transform: 'scaleX(-1)' }}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={capturePhoto}
                        className="flex-1 bg-[#2C4C3B]"
                      >
                        <Camera size={20} className="mr-2" />
                        Capture
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={stopWebcam}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {photoPreview && (
                  <div className="space-y-3">
                    <div className="relative inline-block">
                      <img
                        src={photoPreview}
                        alt="User preview"
                        className="w-32 h-32 rounded-full object-cover border-4 border-[#2C4C3B]"
                      />
                      <button
                        type="button"
                        onClick={removePhoto}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X size={16} weight="bold" />
                      </button>
                    </div>
                    <p className="text-xs text-green-600">✓ Photo ready</p>
                  </div>
                )}
              </div>

              <div>
                <Label>Full Name</Label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={formData.email} disabled className="bg-gray-100" />
                <p className="text-xs text-[#1C1C1A]/60 mt-1">Email cannot be changed</p>
              </div>
              <div>
                <Label>New Password (leave blank to keep current)</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="••••••••"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableRoles().includes('owner') && <SelectItem value="owner">Owner</SelectItem>}
                    {getAvailableRoles().includes('admin') && <SelectItem value="admin">Admin</SelectItem>}
                    {getAvailableRoles().includes('manager') && <SelectItem value="manager">Manager</SelectItem>}
                    {getAvailableRoles().includes('receptionist') && <SelectItem value="receptionist">Receptionist</SelectItem>}
                    {getAvailableRoles().includes('staff') && <SelectItem value="staff">Staff</SelectItem>}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#1C1C1A]/60 mt-1">{ROLE_DESCRIPTIONS[formData.role]}</p>
                {user?.role !== 'admin' && (
                  <p className="text-xs text-orange-600 mt-1">
                    ⓘ You can only assign: {getAvailableRoles().join(', ')}
                  </p>
                )}
              </div>
              
              <div className="border-t border-[#E5E5DF] pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Menu Permissions</Label>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={selectAllPermissions}>
                      Select All
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={clearAllPermissions}>
                      Clear All
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-[#1C1C1A]/60 mb-3">
                  Select which menus this user can access (leave empty to use role defaults)
                </p>
                <div className="space-y-2 border border-[#E5E5DF] rounded-md p-4 bg-[#F9F9F8]">
                  {visibleMenus.map((menu) => (
                    <label
                      key={menu.id}
                      className="flex items-start gap-3 p-3 hover:bg-white rounded-md cursor-pointer border border-transparent hover:border-[#2C4C3B] transition-all"
                    >
                      <input
                        type="checkbox"
                        checked={(formData.permissions || []).includes(menu.id)}
                        onChange={() => togglePermission(menu.id)}
                        className="mt-1 w-4 h-4 text-[#2C4C3B] rounded focus:ring-[#2C4C3B]"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm text-[#2C4C3B]">{menu.name}</p>
                        <p className="text-xs text-[#1C1C1A]/60">{menu.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-blue-600 font-medium mt-2">
                  ✓ {(formData.permissions || []).length} of {visibleMenus.length} menus selected
                </p>
              </div>

              <div className="border-t border-[#E5E5DF] pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Branch Assignment</Label>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={selectAllBranches}>
                      Select All
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={clearAllBranches}>
                      Clear All
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-[#1C1C1A]/60 mb-3">
                  Select which branches this user can access
                  {!isAdmin && ' (limited to your assigned branches)'}
                </p>
                {getAvailableBranches().length === 0 ? (
                  <div className="text-center py-6 text-[#1C1C1A]/60 text-sm border border-[#E5E5DF] rounded-md bg-[#F9F9F8]">
                    No branches available. {isAdmin ? 'Create branches first in Branch Management.' : 'Contact admin to assign branches to you.'}
                  </div>
                ) : (
                  <div className="space-y-2 border border-[#E5E5DF] rounded-md p-4 bg-[#F9F9F8]">
                    {getAvailableBranches().map((branch) => (
                      <label
                        key={branch.branch_id}
                        className="flex items-start gap-3 p-3 hover:bg-white rounded-md cursor-pointer border border-transparent hover:border-[#2C4C3B] transition-all"
                      >
                        <input
                          type="checkbox"
                          checked={(formData.assigned_branches || []).includes(branch.branch_id)}
                          onChange={() => toggleBranch(branch.branch_id)}
                          className="mt-1 w-4 h-4 text-[#2C4C3B] rounded focus:ring-[#2C4C3B]"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-sm text-[#2C4C3B]">{branch.name}</p>
                          {branch.address && <p className="text-xs text-[#1C1C1A]/60">{branch.address}</p>}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                <p className="text-xs text-blue-600 font-medium mt-2">
                  ✓ {(formData.assigned_branches || []).length} of {getAvailableBranches().length} branches selected
                </p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#2C4C3B] hover:bg-[#2C4C3B]/90">
                  Update User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Users;
